import { useState } from "react";
import axios from "axios";
import { Lock, LogOut, RefreshCw, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: "signups", label: "Early access", endpoint: "/admin/signups" },
  { key: "contacts", label: "Contacts", endpoint: "/admin/contacts" },
  { key: "profiles", label: "FitID profiles", endpoint: "/admin/profiles" },
  { key: "funnel", label: "Funnel", endpoint: "/admin/funnel" },
];

export default function Admin() {
  const [token, setToken] = useState(sessionStorage.getItem("fittin_admin_token") || "");
  const [authed, setAuthed] = useState(!!sessionStorage.getItem("fittin_admin_token"));
  const [tab, setTab] = useState("signups");
  const [data, setData] = useState({ signups: null, contacts: null, profiles: null, funnel: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadTab = async (tabKey, tk = token) => {
    const endpoint = TABS.find((t) => t.key === tabKey).endpoint;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}${endpoint}`, {
        headers: { "x-admin-token": tk },
      });
      setData((d) => ({ ...d, [tabKey]: res.data }));
      sessionStorage.setItem("fittin_admin_token", tk);
      setAuthed(true);
    } catch (e) {
      setError(e?.response?.status === 401 ? "Invalid admin token." : "Failed to load.");
      if (e?.response?.status === 401) {
        sessionStorage.removeItem("fittin_admin_token");
        setAuthed(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async () => loadTab("signups", token);

  const switchTab = async (tabKey) => {
    setTab(tabKey);
    if (!data[tabKey]) await loadTab(tabKey);
  };

  const refresh = () => loadTab(tab);

  const logout = () => {
    sessionStorage.removeItem("fittin_admin_token");
    setToken("");
    setAuthed(false);
    setData({ signups: null, contacts: null, profiles: null, funnel: null });
  };

  const exportCsv = () => {
    const current = data[tab];
    if (!current?.entries?.length) return;
    const keys = Object.keys(current.entries[0]);
    const rows = [keys];
    current.entries.forEach((e) => rows.push(keys.map((k) => e[k] ?? "")));
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fittin-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!authed) {
    return (
      <main
        data-testid="admin-page"
        className="min-h-screen bg-[#FAFAFA] grid place-items-center px-6"
      >
        <div
          data-testid="admin-login"
          className="w-full max-w-sm rounded-2xl border border-black/10 bg-white p-8"
        >
          <div className="flex items-center gap-2">
            <span className="h-6 w-6 rounded-full bg-[#0A0A0A] grid place-items-center">
              <Lock className="h-3 w-3 text-white" />
            </span>
            <div className="text-[11px] tracking-[0.22em] uppercase text-neutral-500">
              Fittin Admin
            </div>
          </div>
          <div className="mt-5 text-2xl tracking-tight font-medium">
            Enter admin token
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              login();
            }}
            className="mt-6"
          >
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              data-testid="admin-token-input"
              placeholder="••••••••••"
              className="w-full bg-transparent text-[15px] placeholder:text-neutral-300 text-[#0A0A0A] border-b border-black/15 focus:border-black/70 transition-colors py-2"
            />
            {error && (
              <div
                data-testid="admin-error"
                className="mt-3 text-[12px] text-red-600"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading || !token}
              data-testid="admin-login-submit"
              className="mt-6 w-full rounded-full bg-[#0A0A0A] text-white text-[14px] px-5 py-3 hover:bg-neutral-800 transition-all active:scale-[0.99] disabled:opacity-60"
            >
              {loading ? "Checking…" : "Enter"}
            </button>
          </form>
          <a
            href="/"
            className="mt-6 block text-center text-[12px] text-neutral-500 hover:text-[#0A0A0A]"
          >
            ← Back to site
          </a>
        </div>
      </main>
    );
  }

  const current = data[tab];

  return (
    <main
      data-testid="admin-page"
      className="min-h-screen bg-[#FAFAFA] text-[#0A0A0A]"
    >
      <div className="mx-auto max-w-6xl px-6 md:px-10 py-14">
        <div className="flex items-center justify-between">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-neutral-500">
              <span className="h-px w-8 bg-black/40" />
              Admin
            </div>
            <h1 className="mt-4 text-4xl sm:text-5xl tracking-[-0.035em] font-medium">
              Fittin console
            </h1>
            <div className="mt-2 text-[14px] text-neutral-600">
              {tab === "funnel" ? (
                <>SizingDemo conversion overview</>
              ) : (
                <>
                  <span data-testid="admin-total">{current?.total ?? 0}</span>{" "}
                  {TABS.find((t) => t.key === tab).label.toLowerCase()}
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={refresh}
              disabled={loading}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2 text-[13px] hover:bg-neutral-50 transition-all disabled:opacity-60"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={exportCsv}
              disabled={!current?.entries?.length || tab === "funnel"}
              data-testid="admin-export-csv"
              className="inline-flex items-center gap-2 rounded-full bg-[#0A0A0A] text-white px-4 py-2 text-[13px] hover:bg-neutral-800 transition-all active:scale-[0.98] disabled:opacity-40"
            >
              <Download className="h-3.5 w-3.5" />
              Export CSV
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2 text-[13px] hover:bg-neutral-50 transition-all"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-10 flex items-center gap-2 border-b border-black/10">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => switchTab(t.key)}
              className={`px-4 py-3 text-[13px] -mb-px border-b-2 transition-colors ${
                tab === t.key
                  ? "border-[#0A0A0A] text-[#0A0A0A]"
                  : "border-transparent text-neutral-500 hover:text-[#0A0A0A]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-6 rounded-2xl border border-black/10 bg-white overflow-hidden">
          {tab === "signups" && <SignupsTable entries={current?.entries} loading={loading} />}
          {tab === "contacts" && <ContactsTable entries={current?.entries} loading={loading} />}
          {tab === "profiles" && <ProfilesTable entries={current?.entries} loading={loading} />}
          {tab === "funnel" && <FunnelView data={current} loading={loading} />}
        </div>
      </div>
    </main>
  );
}

function EmptyRow({ loading }) {
  return (
    <div
      data-testid="admin-empty"
      className="px-6 py-16 text-center text-[14px] text-neutral-500"
    >
      {loading ? "Loading…" : "Nothing here yet."}
    </div>
  );
}

function SignupsTable({ entries, loading }) {
  return (
    <>
      <div className="grid grid-cols-12 text-[11px] uppercase tracking-[0.18em] text-neutral-500 px-6 py-4 border-b border-black/5">
        <div className="col-span-5">Email</div>
        <div className="col-span-3">Company</div>
        <div className="col-span-2">Source</div>
        <div className="col-span-2 text-right">Created</div>
      </div>
      {!entries?.length ? (
        <EmptyRow loading={loading} />
      ) : (
        <div data-testid="admin-table">
          {entries.map((e, i) => (
            <div
              key={e.id}
              data-testid={`admin-row-${i}`}
              className="grid grid-cols-12 px-6 py-4 border-b border-black/5 last:border-b-0 hover:bg-neutral-50 transition-colors"
            >
              <div className="col-span-5 text-[14px] font-medium">{e.email}</div>
              <div className="col-span-3 text-[13px] text-neutral-600">{e.company || "—"}</div>
              <div className="col-span-2 text-[13px] text-neutral-600">{e.source || "—"}</div>
              <div className="col-span-2 text-right text-[12px] font-mono text-neutral-500">
                {new Date(e.created_at).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function ContactsTable({ entries, loading }) {
  return (
    <>
      <div className="grid grid-cols-12 text-[11px] uppercase tracking-[0.18em] text-neutral-500 px-6 py-4 border-b border-black/5">
        <div className="col-span-2">Name</div>
        <div className="col-span-3">Email</div>
        <div className="col-span-2">Company</div>
        <div className="col-span-3">Message</div>
        <div className="col-span-2 text-right">Created</div>
      </div>
      {!entries?.length ? (
        <EmptyRow loading={loading} />
      ) : (
        <div data-testid="admin-contacts-table">
          {entries.map((e, i) => (
            <div
              key={e.id}
              data-testid={`admin-contact-row-${i}`}
              className="grid grid-cols-12 px-6 py-4 border-b border-black/5 last:border-b-0 hover:bg-neutral-50 transition-colors"
            >
              <div className="col-span-2 text-[14px] font-medium">{e.name}</div>
              <div className="col-span-3 text-[13px] text-neutral-600">{e.email}</div>
              <div className="col-span-2 text-[13px] text-neutral-600">{e.company || "—"}</div>
              <div className="col-span-3 text-[13px] text-neutral-700 truncate" title={e.message}>
                {e.message}
              </div>
              <div className="col-span-2 text-right text-[12px] font-mono text-neutral-500">
                {new Date(e.created_at).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function ProfilesTable({ entries, loading }) {
  return (
    <>
      <div className="grid grid-cols-12 text-[11px] uppercase tracking-[0.18em] text-neutral-500 px-6 py-4 border-b border-black/5">
        <div className="col-span-4">Email</div>
        <div className="col-span-2">EU size</div>
        <div className="col-span-2">Length (cm)</div>
        <div className="col-span-1">Width</div>
        <div className="col-span-1">Instep</div>
        <div className="col-span-2 text-right">Updated</div>
      </div>
      {!entries?.length ? (
        <EmptyRow loading={loading} />
      ) : (
        <div data-testid="admin-profiles-table">
          {entries.map((e, i) => (
            <div
              key={e.id}
              data-testid={`admin-profile-row-${i}`}
              className="grid grid-cols-12 px-6 py-4 border-b border-black/5 last:border-b-0 hover:bg-neutral-50 transition-colors items-center"
            >
              <div className="col-span-4 text-[14px] font-medium">{e.email}</div>
              <div className="col-span-2 font-mono text-[13px]">
                {e.eu_size ? `EU ${e.eu_size}` : "—"}{" "}
                {e.confidence && (
                  <span className="ml-1 text-[11px] text-neutral-500">({e.confidence})</span>
                )}
              </div>
              <div className="col-span-2 font-mono text-[13px] text-neutral-700">
                {e.foot_length_cm ?? "—"}
              </div>
              <div className="col-span-1 font-mono text-[13px] text-neutral-700">
                {e.foot_width_cm ?? "—"}
              </div>
              <div className="col-span-1 font-mono text-[13px] text-neutral-700">
                {e.foot_circumference_cm ?? "—"}
              </div>
              <div className="col-span-2 text-right text-[12px] font-mono text-neutral-500">
                {new Date(e.updated_at || e.created_at).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function FunnelView({ data, loading }) {
  if (loading || !data) {
    return (
      <div className="px-6 py-16 text-center text-[14px] text-neutral-500">
        {loading ? "Loading…" : "No data."}
      </div>
    );
  }
  const stages = [
    { label: "Started", value: data.sessions_started, kind: "started" },
    { label: "Completed", value: data.sessions_completed, kind: "completed" },
    { label: "Saved profile", value: data.profiles_saved, kind: "saved" },
  ];
  const max = Math.max(1, ...stages.map((s) => s.value));
  const clamp = (n) => Math.min(1, Math.max(0, n));
  const pct = (n) => `${Math.round(clamp(n) * 100)}%`;

  return (
    <div data-testid="admin-funnel" className="p-6 md:p-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        {stages.map((s) => (
          <div
            key={s.kind}
            data-testid={`funnel-stage-${s.kind}`}
            className="rounded-2xl border border-black/10 bg-[#FAFAFA] p-5"
          >
            <div className="text-[11px] uppercase tracking-[0.22em] text-neutral-500">
              {s.label}
            </div>
            <div className="mt-3 text-4xl tracking-[-0.03em] font-medium tabular-nums">
              {s.value}
            </div>
            <div className="mt-3 h-1.5 rounded-full bg-black/5 overflow-hidden">
              <div
                className="h-full rounded-full bg-[#0A0A0A]"
                style={{ width: `${(s.value / max) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat label="Completion rate" hint="of started" value={pct(data.completion_rate)} testid="funnel-completion-rate" />
        <Stat label="Save rate" hint="of completed" value={pct(data.save_rate)} testid="funnel-save-rate" />
        <Stat label="Overall save" hint="of started" value={pct(data.overall_save_rate)} testid="funnel-overall-save-rate" />
      </div>

      <p className="mt-8 text-[12px] text-neutral-500">
        Data sourced from <code className="font-mono">events</code> collection
        (<code className="font-mono">fit_started</code>,{" "}
        <code className="font-mono">fit_completed</code>,{" "}
        <code className="font-mono">profile_saved</code>). De-duplicated by session_id.
      </p>
    </div>
  );
}

function Stat({ label, hint, value, testid }) {
  return (
    <div className="rounded-xl border border-black/10 bg-white p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.22em] text-neutral-500">{label}</div>
      <div className="mt-1.5 text-2xl tracking-tight font-medium tabular-nums">{value}</div>
      <div className="text-[11px] text-neutral-500">{hint}</div>
    </div>
  );
}
